import type { Channel } from 'pusher-js';
import { v4 as uuidv4 } from 'uuid';

import pusherClient from '~/config/initPusher';
import log from '~/common/utils/log';

const CHANNEL_PREFIX = 'client-';
const SUBSCRIPTION_POLL_MS = 50;

export const UNSUBSCRIBE_DELAY_MS = 3_000;

let clientId: string | null = null;
let subscriberCount = 0;
let unsubscribeTimeoutId: ReturnType<typeof setTimeout> | null = null;

/**
 * One id per tab, shared by every order surface. Each panel used to mint its own, so a page
 * held several channels and a panel could submit against one that was not subscribed yet.
 */
export function getOrderClientId(): string {
  clientId ??= uuidv4();

  return clientId;
}

export function getOrderChannelName(): string {
  return `${CHANNEL_PREFIX}${getOrderClientId()}`;
}

/** Pusher owns the truth; a mirrored flag goes stale while a channel is re-subscribing. */
export function isOrderChannelSubscribed(): boolean {
  return pusherClient.channel(getOrderChannelName())?.subscribed === true;
}

export function hasOrderChannelSubscribers(): boolean {
  return subscriberCount > 0;
}

function cancelScheduledUnsubscribe() {
  if (!unsubscribeTimeoutId) return;

  clearTimeout(unsubscribeTimeoutId);
  unsubscribeTimeoutId = null;
}

function connectIfDisconnected(): void {
  const state = pusherClient.connection.state;

  if (state === 'connected' || state === 'connecting') return;

  log('Order channel reconnecting pusher', { state });
  pusherClient.connect();
}

export function subscribeToOrderChannel(): Channel {
  connectIfDisconnected();

  return pusherClient.subscribe(getOrderChannelName());
}

export function acquireOrderChannel(): Channel {
  subscriberCount += 1;
  cancelScheduledUnsubscribe();

  const channelName = getOrderChannelName();

  log('Order channel acquired', { channelName, subscriberCount });

  return subscribeToOrderChannel();
}

/**
 * Unsubscribe is deferred so a panel remount reuses the live channel instead of tearing it
 * down and re-subscribing, which loses any result published in that window.
 */
export function releaseOrderChannel(): void {
  if (subscriberCount === 0) return;

  subscriberCount -= 1;

  if (subscriberCount > 0) return;

  cancelScheduledUnsubscribe();

  unsubscribeTimeoutId = setTimeout(() => {
    unsubscribeTimeoutId = null;

    if (subscriberCount > 0) return;

    const channelName = getOrderChannelName();
    pusherClient.unsubscribe(channelName);

    log('Order channel released', { channelName });
  }, UNSUBSCRIBE_DELAY_MS);
}

export function waitForOrderChannel(timeoutMs: number): Promise<boolean> {
  if (isOrderChannelSubscribed()) return Promise.resolve(true);

  return new Promise((resolve) => {
    const startedAt = Date.now();
    let pollId: ReturnType<typeof setInterval> | null = null;

    const settle = (isSubscribed: boolean) => {
      if (pollId) clearInterval(pollId);
      pollId = null;
      resolve(isSubscribed);
    };

    subscribeToOrderChannel();

    pollId = setInterval(() => {
      if (isOrderChannelSubscribed()) {
        settle(true);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        settle(false);
        return;
      }

      subscribeToOrderChannel();
    }, SUBSCRIPTION_POLL_MS);
  });
}

export function resetOrderChannelForTests(): void {
  cancelScheduledUnsubscribe();
  clientId = null;
  subscriberCount = 0;
}
